import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import styles from './ClinicPatientPlanosAlimentares.module.scss';
import DashboardTableCard from '../components/DashboardTableCard';
import { Row, Col } from 'react-bootstrap';
import Loader from '../components/Loader';
import PatientHeader from '../components/PatientHeader';
import { BsTrash } from 'react-icons/bs';
import { alertModal, confirmModal } from '../utils/modalController';
import { fetchAuth } from '../utils/fetchAuth';

const apiUrl = process.env.REACT_APP_API_URL || '';

const TrashIcon = BsTrash as React.FC<React.SVGProps<SVGSVGElement>>;

const ClinicPatientPlanosAlimentares: React.FC = () => {

    const [user, setUser] = useState<User | null>(null);
    const [age, setAge] = useState<number | null>(null);
    const [planosAlimentares, setPlanosAlimentares] = useState<PlanoAlimentar[]>([]);
    const [loading, setLoading] = useState(true);
    const [creatingPlano, setCreatingPlano] = useState(false);

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            fetchAuth(`${apiUrl}/consultorio/patients/${id}`),
            fetchAuth(`${apiUrl}/consultorio/patients/${id}/planos-alimentares`),
        ])
            .then(async ([patientRes, planosAlimentaresRes]) => {
                if (!patientRes.ok || !planosAlimentaresRes.ok) {
                    throw new Error('Erro ao buscar dados');
                }

                const patientJson = await patientRes.json();
                const planosAlimentaresJson = await planosAlimentaresRes.json();

                if (!patientJson.user) {
                    throw new Error("Usuário não encontrado");
                }

                const userRes = await fetchAuth(`${apiUrl}/consultorio/users/${patientJson.user.id}`);
                if (!userRes.ok) {
                    const error_json = await userRes.json();
                    throw new Error(error_json.message || "Erro desconhecido");
                }

                const userJson = await userRes.json();

                setUser(userJson);
                setAge(userJson?.birthday ? Math.floor((new Date().getTime() - new Date(userJson.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365)) : null);
                setPlanosAlimentares(planosAlimentaresJson);
            })
            .catch((err) => {
                console.error('Erro ao buscar dados:', err);
                alertModal('Erro ao buscar dados: ' + err);
                setUser(null);
                setAge(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id]);

    if (loading) return <Loader />;

    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Consultório</h2>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Row className={styles["row-card"]}>
                            <Col>
                                <PatientHeader
                                    user={user!}
                                    age={age}
                                    breadcrumbItems={[
                                        { label: "Consultório", href: "/consultorio" },
                                        { label: "Pacientes", href: "/consultorio/pacientes" },
                                        { label: user?.name || "Paciente", href: `/consultorio/pacientes/${id}` },
                                        { label: "Planos Alimentares" },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            <DashboardTableCard
                welcomeMessage={`Planos Alimentares`}
                tableData={{
                    headings: ['Plano Alimentar', 'Data de Início', 'Data de Término', 'Ações'],
                    data: [
                        [<a
                            href="#"
                            className={styles["link"]}
                            onClick={async (e) => {
                                e.preventDefault();
                                if (creatingPlano) return;
                                setCreatingPlano(true);

                                fetchAuth(`${apiUrl}/consultorio/planos-alimentares`, {
                                    method: 'POST',
                                    body: JSON.stringify({ 'patient-id': id }),
                                })
                                    .then(async (res) => {
                                        if (!res.ok) {
                                            const error_json = await res.json();
                                            throw new Error(error_json.message || 'Erro ao criar plano alimentar');
                                        }
                                        return res.json();
                                    })
                                    .then(data => {
                                        navigate(`/consultorio/pacientes/${id}/planos-alimentares/${data.id}`);
                                    })
                                    .catch(err => {
                                        throw new Error('Erro ao criar plano alimentar: ' + err);
                                    })
                                    .finally(() => {
                                        setCreatingPlano(false);
                                    });
                            }}
                        >{creatingPlano ? 'Criando plano alimentar...' : 'Novo plano alimentar'}</a>],
                        ...planosAlimentares.map((plano) => [
                            <Link to={`/consultorio/pacientes/${id}/planos-alimentares/${plano.id}`} className={styles["link"]} key={plano.id}>{plano.nome}</Link>,
                            plano.data_inicio ? new Date(plano.data_inicio).toLocaleDateString('pt-BR') : '',
                            plano.data_termino ? new Date(plano.data_termino).toLocaleDateString('pt-BR') : '',
                            <a
                                href='#'
                                onClick={async (e) => {
                                    e.preventDefault();
                                    if (!await confirmModal('Tem certeza que deseja excluir este plano alimentar? Esta ação não pode ser desfeita.')) return;

                                    const updatedPlanosAlimentares = planosAlimentares.filter(p => p.id !== plano.id);
                                    fetchAuth(`${apiUrl}/consultorio/planos-alimentares/${plano.id}`, {
                                        method: 'DELETE'
                                    })
                                        .then(async (res) => {
                                            if (!res.ok) {
                                                const error_json = await res.json();
                                                throw new Error(error_json.message || 'Erro ao excluir plano alimentar');
                                            }
                                            return res.json();
                                        })
                                        .then(data => {
                                            setPlanosAlimentares(updatedPlanosAlimentares);
                                        })
                                        .catch(err => {
                                            console.error('Erro ao excluir plano alimentar:', err);
                                            alertModal('Erro ao excluir plano alimentar: ' + err);
                                        });
                                }}
                            ><TrashIcon /></a>
                        ]),
                    ]
                }}
            />
        </>
    );
};

export default ClinicPatientPlanosAlimentares;
